import type { BannerPolicy, ConsentDecision } from "@/core/consent/domain/consent.types";
import type { ConsentSource } from "@/core/consent/sources/consent.source";
import { logger } from "@/core/logging";
import type {
  HubSpotCommand,
  HubSpotConsent,
  HubSpotConsentCategories,
  HubSpotPrivacySettings,
} from "@/core/consent/sources/hubspot/hubspot.types";

/**
 * HubSpot writes these first-party cookies only after the visitor actually
 * answers the banner (accept, decline, or a per-category save). In opt-in
 * mode the consent listener reports every category as `false` BEFORE any
 * choice, so the categories alone cannot distinguish "declined" from "never
 * answered" — one of these cookies is what makes a reported state an explicit
 * choice. `__hs_opt_out` is HubSpot's older decline cookie: the current banner
 * deletes it on load and asks again under the category model, so it only
 * matters in the window before the banner runs — kept for parity with the
 * legacy adapter, which checks both.
 */
export const HUBSPOT_CHOICE_COOKIES: readonly string[] = ["__hs_cookie_cat_pref", "__hs_opt_out"];

/** HubSpot's category ids inside `__hs_cookie_cat_pref` (1/2/3). */
const HUBSPOT_CAT_PREF_ANALYTICS = "1";
const HUBSPOT_CAT_PREF_ADVERTISEMENT = "2";
const HUBSPOT_CAT_PREF_FUNCTIONALITY = "3";

export function hasHubSpotBannerChoiceCookie(): boolean {
  if (typeof document === "undefined") {
    return false;
  }

  return document.cookie
    .split(";")
    .some((cookie) => HUBSPOT_CHOICE_COOKIES.some((name) => cookie.trim().startsWith(`${name}=`)));
}

function readCookieValue(name: string): string | null {
  if (typeof document === "undefined") {
    return null;
  }

  const prefix = `${name}=`;
  for (const part of document.cookie.split(";")) {
    const trimmed = part.trim();
    if (trimmed.startsWith(prefix)) {
      return decodeURIComponent(trimmed.slice(prefix.length));
    }
  }
  return null;
}

/**
 * Categories the visitor stored in HubSpot's choice cookie. `__hs_cookie_cat_pref`
 * is `1:true_2:false_3:true` (analytics / advertisement / functionality; `_` or
 * `,` between pairs) and wins when present. The older `__hs_opt_out` carries the
 * answer to a single accept/decline banner: `yes` is a decline of every optional
 * category, `no` is the older accept (every optional category granted). Any
 * other value, or nothing parseable, returns `null` — fail closed.
 */
export function readHubSpotChoiceCategories(): HubSpotConsentCategories | null {
  const catPref = readCookieValue("__hs_cookie_cat_pref");
  if (catPref !== null && catPref.length > 0) {
    const entries = new Map<string, boolean>();
    for (const item of catPref.split(/[_,]/)) {
      if (item.length === 0) continue;
      const separator = item.indexOf(":");
      if (separator <= 0) return null;
      const key = item.slice(0, separator).trim();
      const raw = item.slice(separator + 1).trim();
      if (raw !== "true" && raw !== "false") return null;
      entries.set(key, raw === "true");
    }
    const analytics = entries.get(HUBSPOT_CAT_PREF_ANALYTICS);
    const advertisement = entries.get(HUBSPOT_CAT_PREF_ADVERTISEMENT);
    const functionality = entries.get(HUBSPOT_CAT_PREF_FUNCTIONALITY);
    if (analytics === undefined || advertisement === undefined || functionality === undefined) {
      return null;
    }
    return { analytics, advertisement, functionality };
  }

  const optOut = readCookieValue("__hs_opt_out")?.trim().toLowerCase();
  if (optOut === "yes") {
    return { analytics: false, advertisement: false, functionality: false };
  }
  if (optOut === "no") {
    return { analytics: true, advertisement: true, functionality: true };
  }

  return null;
}

const categoriesEqual = (a: HubSpotConsentCategories, b: HubSpotConsentCategories): boolean =>
  a.analytics === b.analytics &&
  a.advertisement === b.advertisement &&
  a.functionality === b.functionality;

/**
 * `_hsp` is a pre-load command queue: pushing is safe before the HubSpot
 * loader script arrives, because an early push sits in the array until the
 * loader replaces `push` and drains it. So every command here — the listener
 * registration and `showBanner` — is queued rather than dropped when it
 * arrives first.
 */
export function pushHubSpotCommand(command: HubSpotCommand): void {
  window._hsp = window._hsp || [];
  window._hsp.push(command);
}

/**
 * HubSpot exposes no readable consent global and its privacy consent listener
 * cannot be unregistered, so ONE module-level listener caches the latest
 * report and fans out to whichever sources are currently started — the same
 * shape as the c15t source's module-level slot, for the same reason.
 * `null` until HubSpot's banner has initialized and reported once.
 */
let latestReport: HubSpotConsent | null = null;
/**
 * The portal policy HubSpot matched for this page, from its privacy settings
 * listener. HubSpot matches the policy once per page (after its own
 * geolocation lookup) and before its first consent report, so it is known
 * before any answer arrives. `null` until then.
 */
let latestSettings: HubSpotPrivacySettings | null = null;
let listenerRegistered = false;
const subscribers = new Set<() => void>();

function registerHubSpotListener(): void {
  if (listenerRegistered) return;
  listenerRegistered = true;

  // Registered first so a replay reaches us in HubSpot's own order: it matches
  // the page's policy once, then reports consent. Replayed at once when HubSpot
  // has already matched.
  pushHubSpotCommand([
    "addPrivacySettingsListener",
    (settings: HubSpotPrivacySettings) => {
      latestSettings = settings;
    },
  ]);

  // The callback fires with the current state once the banner initializes and
  // again whenever the visitor changes it.
  pushHubSpotCommand([
    "addPrivacyConsentListener",
    (consent: HubSpotConsent) => {
      latestReport = consent;
      subscribers.forEach((notify) => notify());
    },
  ]);
}

/** HubSpot's own names for its policy type numbers, as `banner.js` defines them. */
const HUBSPOT_POLICY_TYPE_NAMES: Readonly<Record<number, string>> = {
  0: "COOKIES_WITHOUT_BANNER",
  1: "NO_COOKIES",
  2: "NOTIFY",
  3: "OPT_IN",
  4: "COOKIES_BY_CATEGORY",
  5: "OPT_OUT",
  6: "OPT_OUT_BY_CATEGORY",
};

/**
 * Policy modes whose rules grant before the visitor answers: an opt-out banner
 * tracks until the visitor declines, and a notice only informs. Before an
 * answer HubSpot reports what such a policy grants, which is its default.
 * Opt-in, by-category, no-cookies and cookies-without-banner policies apply no
 * default: they show no banner, or ask first.
 */
const POLICY_MODES_THAT_DEFAULT_TO_CONSENT: ReadonlySet<string> = new Set([
  "OPT_OUT",
  "OPT_OUT_BY_CATEGORY",
  "NOTIFY",
]);

/**
 * HubSpot's matched policy as engine evidence: the id from the settings
 * listener, kept as HubSpot's own string form, and the mode named from the
 * policy's type number. A missing id or an unknown type is "no policy
 * reported", never a guessed one.
 */
export function toBannerPolicy(
  settings: HubSpotPrivacySettings | null | undefined,
  matchedBannerType: number | undefined,
): BannerPolicy | null {
  const id = settings?.id;
  if ((typeof id !== "number" && typeof id !== "string") || String(id).length === 0) {
    return null;
  }
  const mode =
    matchedBannerType === undefined ? undefined : HUBSPOT_POLICY_TYPE_NAMES[matchedBannerType];
  if (mode === undefined) {
    return null;
  }

  return { provider: "hubspot", id: String(id), mode };
}

/**
 * HubSpot writes `matchedBannerType` onto `rawPerfMetrics` before it notifies
 * consent listeners. `getPerfMetrics()` keeps returning undefined until
 * `hsCookieBannerLoaded`, and `banner.js` dispatches that event only after
 * `run()` — so the startup replay, including a stored choice, happens while
 * the ready snapshot is withheld. Reading `.matchedBannerType` off that
 * undefined snapshot throws inside the listener, and HubSpot swallows it, so
 * the answer never reaches the engine.
 */
function readMatchedBannerType(): number | undefined {
  const banner = window.hsCookieBanner;
  if (banner === undefined) {
    return undefined;
  }

  const readyType = banner.getPerfMetrics?.()?.matchedBannerType;
  if (readyType !== undefined) {
    return readyType;
  }

  return banner.rawPerfMetrics?.matchedBannerType;
}

/**
 * The policy an answer is being given under. The settings listener's own
 * `mode` reports every type except `NO_COOKIES` as `OPT_IN`, so the type is
 * read from HubSpot's banner metrics. A matched policy that can't be read is
 * logged and reported as no policy.
 */
function readCurrentBannerPolicy(): BannerPolicy | null {
  if (latestSettings === null) {
    return null;
  }

  const policy = toBannerPolicy(latestSettings, readMatchedBannerType());
  if (policy === null) {
    logger.error("ageless_consent_hubspot_policy_unreadable");
  }
  return policy;
}

/** Test-only reset of the module-level listener cache. */
export function resetHubSpotSourceForTests(): void {
  latestReport = null;
  latestSettings = null;
  listenerRegistered = false;
  subscribers.clear();
}

/**
 * Map HubSpot's three optional categories onto the engine's four scopes:
 *
 * - `functionality` → `functional`
 * - `analytics`     → `analytics`
 * - `advertisement` → `advertising` AND `dataSharing` (HubSpot has no
 *   separate sharing category, and ads almost always imply sharing)
 *
 * HubSpot does not model "strictly necessary": its own cookies are always
 * allowed and only the three optional categories are reported.
 *
 * This is the visitor's RAW answer. Global Privacy Control is deliberately not
 * applied here: the engine owns the clamp and its provenance (see
 * `reduceConsent`), and a source that pre-clamped would both duplicate that
 * rule and hide from the engine that the answer and the effective decision
 * differ — which is exactly what the persisted answer / effective decision
 * split exists to preserve. (The legacy adapter clamps itself; this source
 * must not.)
 */
export const mapHubSpotConsent = (consent: HubSpotConsent): ConsentDecision => ({
  functional: consent.categories.functionality === true,
  analytics: consent.categories.analytics === true,
  advertising: consent.categories.advertisement === true,
  dataSharing: consent.categories.advertisement === true,
});

const decisionsEqual = (a: ConsentDecision, b: ConsentDecision): boolean =>
  a.functional === b.functional &&
  a.analytics === b.analytics &&
  a.advertising === b.advertising &&
  a.dataSharing === b.dataSharing;

/**
 * The HubSpot cookie banner as a consent source. Emits `bannerAnswered` with
 * the mapped answer whenever HubSpot HAS reported and the visitor has
 * explicitly chosen, and that answer differs from the last one this source
 * emitted — including HubSpot's re-report of a stored choice on page load,
 * which the reducer then dedupes against the engine's own restored answer (or
 * adopts, if the engine has none).
 *
 * Before an answer it emits only a policy's default: under an opt-out or
 * notice policy, what HubSpot reports is what that policy grants until the
 * visitor declines, so it is emitted as `policyDefaultReported`. Otherwise it
 * is silent until decided: in opt-in mode HubSpot reports every category as
 * `false` before an answer, and emitting that would be indistinguishable from
 * a real decline. The engine's `unresolved` is the tri-state, so "not asked
 * yet" needs no representation here. What makes a report an explicit choice
 * is HubSpot's own choice cookie, checked at report time: the banner writes
 * it BEFORE it notifies listeners, so the report that follows an answer sees
 * it. The cookie alone is never enough — a returning visitor whose HubSpot
 * script is blocked or still loading has the cookie while nothing has
 * reported, and trusting it would resolve a state HubSpot never sent.
 *
 * The report must also MATCH the cookie. On a hostname with no portal cookie
 * policy HubSpot reports every category granted and writes no choice cookie;
 * a prior root-domain choice from `www` is still visible, and trusting that
 * cookie's presence with the uncovered-host grant report would flip a decline
 * into analytics activation. Covered hosts write matching categories before
 * notifying, so the match holds there.
 *
 * HubSpot's choice cookie is root-domain scoped, so a decision made on a
 * HubSpot-hosted landing page under the same domain is reported here as
 * already decided — consent stays continuous across those pages without any
 * import step.
 */
export const createHubSpotSource = (): ConsentSource => ({
  start(emit) {
    if (typeof window === "undefined") {
      return () => {};
    }

    registerHubSpotListener();

    // What was last emitted, by kind: the visitor's answer matching a default
    // already emitted is still their own act and must be emitted.
    let lastEmitted: { readonly isDefault: boolean; readonly decision: ConsentDecision } | null =
      null;

    const syncPolicyDefault = (report: HubSpotConsent): void => {
      // No matched policy (a host the portal doesn't cover) reports every
      // category granted too, and that is no policy's default.
      if (latestSettings === null) {
        return;
      }

      const bannerPolicy = readCurrentBannerPolicy();
      if (bannerPolicy === null || !POLICY_MODES_THAT_DEFAULT_TO_CONSENT.has(bannerPolicy.mode)) {
        return;
      }

      const decision = mapHubSpotConsent(report);
      if (lastEmitted?.isDefault === true && decisionsEqual(lastEmitted.decision, decision)) {
        return;
      }

      lastEmitted = { isDefault: true, decision };
      emit({ type: "policyDefaultReported", decision, bannerPolicy });
    };

    const sync = (): void => {
      const report = latestReport;
      if (report === null) {
        return;
      }

      // No choice cookie means the visitor hasn't answered; what HubSpot
      // reports then is the matched policy's default, if it sets one.
      if (!hasHubSpotBannerChoiceCookie()) {
        syncPolicyDefault(report);
        return;
      }

      const expected = readHubSpotChoiceCategories();
      if (expected === null || !categoriesEqual(report.categories, expected)) {
        return;
      }

      const decision = mapHubSpotConsent(report);
      if (lastEmitted?.isDefault === false && decisionsEqual(lastEmitted.decision, decision)) {
        return;
      }

      // The policy showing now is the one this answer was given under: an
      // answer only exists after HubSpot matched a policy and rendered it.
      // Read it before remembering the decision, so a throw cannot consume
      // the answer and then ignore the retry.
      const bannerPolicy = readCurrentBannerPolicy();
      lastEmitted = { isDefault: false, decision };
      emit({ type: "bannerAnswered", decision, bannerPolicy });
    };

    // HubSpot may have reported before this source started (a second
    // composition, StrictMode's remount), so the cached report is read once
    // up front rather than waiting for the next change.
    sync();
    subscribers.add(sync);
    return () => {
      subscribers.delete(sync);
    };
  },
});

/**
 * Reopen HubSpot's banner so a visitor can revisit a decision in either
 * direction — including turning a decline into an accept. `showBanner` rather
 * than `revokeCookieConsent`: the latter is one-way and would leave an
 * accepting visitor with no path back. Safe before the loader has arrived:
 * the `_hsp` queue holds the command until HubSpot drains it.
 */
export const reopenHubSpotBanner = (): void => pushHubSpotCommand(["showBanner"]);

/**
 * Forget HubSpot's stored choice in this browser (diagnostics only — the
 * snap debug panel's "clear consent"). HubSpot keeps the choice in cookies it
 * sets on the root domain, so each candidate domain is expired: the bare
 * host, the host itself, and its registrable domain.
 */
export const clearHubSpotChoice = (): void => {
  if (typeof document === "undefined") {
    return;
  }

  const host = window.location.hostname;
  const rootDomain = host.split(".").slice(-2).join(".");
  const domains = ["", host, `.${host}`, rootDomain, `.${rootDomain}`];
  for (const name of HUBSPOT_CHOICE_COOKIES) {
    for (const domain of domains) {
      document.cookie = `${name}=; Max-Age=0; path=/${domain ? `; domain=${domain}` : ""}`;
    }
  }
};
